/*
    API Access (static#49) — secret-handling guards.

    The IAM secret access key returned by POST /api-access/provision and
    POST /api-access/rotate is UNRECOVERABLE. It is shown to the customer once
    and then dropped. Two things must therefore never happen:

      1. It must never reach the Redux store. src/store/index.js persists the
         `authUser` slice to localStorage under the hardcoded AES key
         "my-secret-key", which is equivalent to writing it to disk in the
         clear. `stripSecrets` is the belt-and-braces guard applied in both the
         thunk and the reducer.

      2. It must never reach a log line or a serialized error. Axios attaches
         the full HTTP response to its error objects, so the repo's usual
         `console.log("error: ", error)` on a provision call would dump the
         secret straight into the browser console. `safeErrorMessage` extracts a
         plain string and nothing else; the api-access thunks log only that.
*/

// Field names that may carry credential material, in both the camelCase shape
// the backend contract uses and the snake_case shape a raw DB row would have.
export const SECRET_FIELDS = [
  "secretAccessKey",
  "secret_access_key",
  "accessKeyId",
  "access_key_id",
  "secret",
  "secretKey",
  "secret_key",
];

const MAX_MESSAGE_LENGTH = 300;

/**
 * Deep copy of `value` with every credential-bearing field removed.
 * Anything that is not a plain object/array is returned as-is.
 */
export const stripSecrets = (value) => {
  if (Array.isArray(value)) {
    return value.map(stripSecrets);
  }
  if (value === null || typeof value !== "object") {
    return value;
  }
  return Object.keys(value).reduce((acc, key) => {
    if (SECRET_FIELDS.indexOf(key) === -1) {
      acc[key] = stripSecrets(value[key]);
    }
    return acc;
  }, {});
};

/**
 * A displayable, log-safe string for an error from an api-access call.
 * Reads ONLY known string fields — never the response body as a whole.
 */
export const safeErrorMessage = (error, fallback = "Something went wrong") => {
  const candidates = [error?.response?.data?.message, error?.message];
  const found = candidates.find((c) => typeof c === "string" && c.trim());
  return String(found || fallback).slice(0, MAX_MESSAGE_LENGTH);
};

/**
 * The machine-readable error code, if the backend sent one (e.g.
 * "ROTATION_IN_PROGRESS"). Returns null rather than an object, always.
 */
export const safeErrorCode = (error) => {
  const code = error?.response?.data?.code;
  return typeof code === "string" ? code.slice(0, 64) : null;
};

/**
 * The one field we are willing to read off an error body besides message/code:
 * the previous key's expiry, used for the friendly "rotation already in
 * progress" state. Returns a string or null.
 */
export const safeErrorPrevExpiresAt = (error) => {
  const value = error?.response?.data?.prevExpiresAt;
  return typeof value === "string" ? value.slice(0, 64) : null;
};
